import { API_BASE_URL } from '../constants'

let cachedToken: string | null = null

/**
 * Sets the authentication token to be used for subsequent API calls.
 * This should be called before any calls to fetchWithAuth if the token
 * is managed externally (e.g., in a worker environment).
 *
 * @param {string} token The authentication token.
 */
export function setToken(token: string | null): void {
  cachedToken = token
}

/**
 * Gets the cached authentication token.
 * @returns {string | null} The cached token or null if not set.
 */
export function getToken(): string | null {
  return cachedToken
}

// Helper function to wrap fetch calls with authentication
async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  if (!cachedToken) {
    // If running in an environment where getAuthToken was expected,
    // this indicates it hasn't been initialized via setToken.
    console.error('Authentication token has not been set. Call setToken first.')
    console.error('Current cached token:', cachedToken)
    console.error(
      'Make sure authentication is complete before making API calls'
    )
    throw new Error('Authentication token not available.')
  }

  const headers = new Headers(options.headers || {}) // Create Headers object
  headers.set('Authorization', `Bearer ${cachedToken}`)
  if (
    options.body &&
    !(options.body instanceof FormData) &&
    !headers.has('Content-Type')
  ) {
    // Set default Content-Type for JSON bodies if not already set
    headers.set('Content-Type', 'application/json')
  }

  const fetchOptions: RequestInit = {
    ...options,
    headers,
  }

  const response = await fetch(url, fetchOptions)

  // Basic error handling for non-OK responses
  if (!response.ok) {
    let errorData = { message: 'An unknown error occurred' }
    try {
      // Attempt to parse JSON error body
      errorData = await response.json()
    } catch (e) {
      // If parsing fails, use status text
      errorData.message = `Request failed with status ${response.status}: ${response.statusText}`
    }
    console.error('API Request Failed:', {
      url,
      status: response.status,
      errorData,
    })
    // Throw an error that includes status and potentially parsed details
    throw new Error(
      `API Error (${response.status}): ${JSON.stringify(errorData)}`
    )
  }

  return response
}

export class PrepareAPIService {
  public client: typeof FlatfileProxyClient = FlatfileProxyClient
  public axios: any = { post: () => {} }
}

/**
 * Drop in replacement for the FlatfileClient that targets the Prepare API
 */
export const FlatfileProxyClient = {
  artifacts: {
    list: async (params?: {
      projectId?: string
      fileId?: string
      sheetId?: string
    }) => {
      let url = `${import.meta.env.VITE_API_URL}/artifacts`
      const queryParams = new URLSearchParams()

      if (params?.projectId) queryParams.append('projectId', params.projectId)
      if (params?.fileId) queryParams.append('fileId', params.fileId)
      if (params?.sheetId) queryParams.append('sheetId', params.sheetId)

      const queryString = queryParams.toString()
      if (queryString) url += `?${queryString}`

      const response = await fetchWithAuth(url)
      const artifacts = await response.json()
      return artifacts
    },
    get: async (id: string) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/artifacts/${id}`
      )
      const artifact = await response.json()
      return artifact
    },
    getFileAnalysis: async (fileId: string) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/artifacts/files/${fileId}/analysis`
      )
      const artifact = await response.json()
      return artifact
    },
  },
  projects: {
    get: async () => {
      const projectsResponse = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/projects`
      )
      const projects = await projectsResponse.json()
      return {
        // Prepare API returns projects[], not { data: { projects } }
        data: projects, // Adjust based on actual API response structure
      }
    },
    create: async (name: string) => {
      const body = {
        name: name || 'Prepare Chat ' + new Date().toISOString(),
        description: '',
        blueprint: {},
        metadata: {},
      }
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/projects`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      )
      const project = await response.json()
      return project
    },
    list: async () => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/projects`
      )
      const projects = await response.json()
      return projects
    },
    update: async (projectId: string, config: { name?: string }) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/projects/${projectId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(config),
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      const project = await response.json()
      return { data: project }
    },
  },
  workbooks: {
    list: async ({ spaceId }: { spaceId: string }) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/workbooks?projectId=${spaceId}`
      )
      const workbooks = await response.json()
      return {
        data: workbooks.map((workbook: any) => ({
          ...workbook,
          name: '[file] ' + workbook.name,
          labels: ['file'],
        })),
      }
    },
    get: async (workbookId: string) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/workbooks/${workbookId}`
      )
      const workbook = await response.json()
      return {
        data: workbook,
      }
    },
    create: async (config: { spaceId: string; name: string }) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/workbooks`,
        {
          method: 'POST',
          body: JSON.stringify({
            name: config.name,
            projectId: config.spaceId,
            fileId: (config as any).fileId,
            schema: (config as any).schema,
          }),
        }
      )
      const workbook = await response.json()
      return { data: workbook }
    },
  },
  sheets: {
    list: async (fileId: string) => {
      // Validate fileId before making the request
      if (!fileId || fileId === 'undefined' || fileId === '') {
        console.error('Invalid fileId provided to sheets.list:', fileId)
        return { data: [] }
      }
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/files/${fileId}/sheets`
      )
      const sheets = await response.json()
      return {
        data: sheets,
      }
    },
    get: async (sheetId: string) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/sheets/${sheetId}`
      )
      const sheet = await response.json()
      return {
        data: sheet,
      }
    },
    create: async (config: {
      fileId: string
      name: string
      schema: Record<string, any>
    }) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/files/${config.fileId}/sheets`,
        {
          method: 'POST',
          body: JSON.stringify({ name: config.name, schema: config.schema }),
        }
      )
      const sheet = await response.json()
      return { data: sheet }
    },
    update: async (
      sheetId: string,
      config: {
        name?: string
        schema?: Record<string, any>
        enrichments?: Record<string, any>
        metadata?: Record<string, any>
        rules?: Record<string, any>
        addedRules?: Record<string, any>
      }
    ) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/sheets/${sheetId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(config),
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      const sheet = await response.json()
      return { data: sheet }
    },
  },
  files: {
    list: async (projectId: string) => {
      // Accept projectId within an object like other methods
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/files?projectId=${projectId}`
      )
      const files = await response.json()
      return {
        data: files,
      }
    },
    delete: async (fileId: string) => {
      const filesResponse = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/files/${fileId}`,
        {
          method: 'DELETE',
        }
      )
      const files = await filesResponse.json()
      return { data: files }
    },
    update: async (
      fileId: string,
      config: {
        name?: string
        environmentId?: string
        projectId?: string
        origin?: string
      }
    ) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/files/${fileId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(config),
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      const file = await response.json()
      return { data: file }
    },
    create: async (
      file: File,
      environmentId: string,
      projectId: string,
      origin?: string,
      onProgress?: (progress: number) => void
    ) => {
      const formData = new FormData()
      formData.append('file', file, file.name)
      formData.append('name', file.name) // Add explicit name field
      formData.append('environmentId', environmentId || '')
      formData.append('projectId', projectId || '')
      if (origin) formData.append('origin', origin)

      if (!cachedToken) {
        console.error(
          'Authentication token has not been set. Call setToken first.'
        )
        console.error('Current cached token:', cachedToken)
        console.error(
          'Make sure authentication is complete before making API calls'
        )
        throw new Error('Authentication token not available.')
      }

      const authToken = cachedToken

      const xhr = new XMLHttpRequest()
      const url = `${import.meta.env.VITE_API_URL}/files`

      return new Promise((resolve, reject) => {
        xhr.open('POST', url, true)
        xhr.setRequestHeader('Authorization', `Bearer ${authToken}`)

        xhr.upload.onprogress = (event) => {
          if (onProgress && event.lengthComputable) {
            onProgress((event.loaded / event.total) * 100)
          }
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText))
          } else {
            let errorMessage = `Upload failed: ${xhr.statusText}`
            try {
              const errorData = JSON.parse(xhr.responseText)
              errorMessage =
                errorData.message || errorData.error || errorMessage
            } catch (e) {
              // If response is not JSON, use default message
            }
            reject(new Error(errorMessage))
          }
        }

        xhr.onerror = () => reject(new Error('Network error during upload'))
        xhr.send(formData)
      })
    },
    // Add other file operations here if needed (get, create/upload, delete)
  },
  threads: {
    get: async (threadId: string) => {
      const threadsResponse = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/threads/${threadId}`
      )
      const thread = await threadsResponse.json()
      // Prepare API returns the thread directly
      return {
        data: thread, // Adjusted response structure
      }
    },
    list: async (projectId: string) => {
      const threadsResponse = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/threads?projectId=${projectId}` //projectId=${projectId}&resourceId=${projectId}
      )
      const threads = await threadsResponse.json()
      return threads
    },
    create: async (
      sourceSheetId: string,
      metadata: Record<string, string>,
      projectId: string,
      type: string
    ) => {
      const body = {
        source: sourceSheetId,
        resourceId: projectId,
        name: 'Prepare Chat ' + new Date().toISOString(),
        projectId: projectId,
        metadata: {
          description: 'Prepare Chat',
          ...metadata,
        },
        type,
      }
      const response = await fetchWithAuth(`${API_BASE_URL}/threads`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      const thread = await response.json()
      return thread
    },
    update: async (
      threadId: string,
      state?: Record<string, any>,
      operations?: Record<string, any>[],
      currentRules?: Record<string, any>[]
    ) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/threads/${threadId}`,
        {
          method: 'PUT',
          body: JSON.stringify({ state, operations, currentRules }),
        }
      )
      const thread = await response.json()
      // Prepare API returns the updated thread directly
      return {
        data: thread, // Adjusted response structure
      }
    },
  },
  messages: {
    list: async (threadId: string) => {
      const messagesResponse = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/threads/${threadId}/messages`
      )
      const messages = await messagesResponse.json()
      return {
        // Assuming Prepare API returns messages compatible with the expected format
        // (or perform necessary mapping here)
        data: messages.map((message: any) => ({
          // Example mapping, adjust as needed based on actual API response
          ...message,
          content:
            typeof message.content === 'object' && message.content.text
              ? message.content.text
              : message.content,
          role: message.role || 'user', // Ensure role exists
          type: message.type || 'text', // Ensure type exists
        })),
      }
    },
    create: async (threadId: string, message: any) => {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/threads/${threadId}/messages`,
        {
          method: 'POST',
          body: JSON.stringify(message),
        }
      )
      const messageResult = await response.json()
      return { data: messageResult }
    },
    update: async (threadId: string, messageId: string, message: any) => {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/threads/${threadId}/messages/${messageId}`,
        {
          method: 'PUT',
          body: JSON.stringify(message),
        }
      )
      const messageResult = await response.json()
      return { data: messageResult }
    },
  },
  agent: {
    suggestReroll: async (config: {
      sheetId: string
      threadId: string
      userPrompt: string
      messageId?: string
      columnConfig: any[]
    }) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/agent/suggest/reroll`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(config),
        }
      )
      const result = await response.json()
      return { data: result }
    },
    suggest: async (config: {
      sheetId: string
      threadId: string
      userPrompt: string
      columnConfig: any[]
    }) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/agent/suggest`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(config),
        }
      )
      const result = await response.json()
      return { data: result }
    },
  },
  records: {
    update: async (sheetId: string, recordsToUpdate: any[]) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/sheets/${sheetId}/records`,
        {
          method: 'POST', // Using POST to align with the provided API endpoint
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ records: recordsToUpdate }),
        }
      )
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message:
            'Failed to update records and server returned non-JSON or no response body',
        }))
        console.error('Failed to update records:', errorData, response.status)
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        )
      }
      return response.json()
    },
  },
  enrichments: {
    research: async (config: { query: string; provider: string }) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/enrichments/research`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(config),
        }
      )
      const result = await response.json()
      return { data: result }
    },
    researchInngest: async (config: {
      sheetId: string
      enrichment: any
      columnConfig: any[]
    }) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/enrichments/research-inngest`,
        {
          method: 'POST',
          body: JSON.stringify(config),
        }
      )
      const result = await response.json()
      return { data: result }
    },
    prompt: async (config: {
      sheetId: string
      enrichment: any
      columnConfig: any[]
    }) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/enrichments/prompt`,
        {
          method: 'POST',
          body: JSON.stringify(config),
        }
      )
      const result = await response.json()
      return { data: result }
    },
    translate: async (config: {
      sheetId: string
      enrichment: any
      columnConfig: any[]
    }) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/enrichments/translate`,
        {
          method: 'POST',
          body: JSON.stringify(config),
        }
      )
      const result = await response.json()
      return { data: result }
    },
    toggleBackground: async (
      sheetId: string,
      enrichmentKey: string,
      enabled: boolean
    ) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/sheets/${sheetId}/enrichments/${enrichmentKey}/background`,
        {
          method: 'PATCH',
          body: JSON.stringify({ enabled }),
        }
      )
      const result = await response.json()
      return { data: result }
    },
    list: async (sheetId: string) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/sheets/${sheetId}/enrichments`
      )
      const result = await response.json()
      return { data: result }
    },
    getStatus: async (sheetId: string) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/enrichments/sheets/${sheetId}/enrichments/status`
      )
      const result = await response.json()
      return result
    },
    triggerManual: async (
      sheetId: string,
      config: {
        recordIds: string[]
        enrichmentIds?: string[]
        priority?: 'low' | 'normal' | 'high' | 'critical'
      }
    ) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/enrichments/sheets/${sheetId}/enrichments/trigger`,
        {
          method: 'POST',
          body: JSON.stringify(config),
        }
      )
      const result = await response.json()
      return { data: result }
    },
    getQueueStatus: async () => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/enrichments/queue-status`
      )
      const result = await response.json()
      return result
    },
    getConfiguration: async (sheetId: string, enrichmentKey: string) => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/enrichments/sheets/${sheetId}/enrichments/${enrichmentKey}`
      )
      const result = await response.json()
      return result
    },
  },
  metrics: {
    getUserStats: async () => {
      const response = await fetchWithAuth(
        `${import.meta.env.VITE_API_URL}/users/stats`
      )
      const stats = await response.json()
      return { data: stats }
    },
  },
}
